import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useSettingsStore } from './SettingsStore'

const TIMER_INTERVAL = 50 // ms

// --- Start of Web Worker integration ---
let timerWorker: Worker | null = null

// Function to initialize the worker and set up message handling
const initializeWorker = (tick: () => void) => {
  // Ensure this only runs in the browser
  if (typeof window !== 'undefined' && !timerWorker) {
    timerWorker = new Worker('/timer.worker.js')
    timerWorker.onmessage = e => {
      if (e.data.type === 'tick') {
        tick()
      }
    }
  }
}

// --- End of Web Worker integration ---

type TimerStatus = 'idle' | 'running' | 'paused'
type TimerMode = 'focus' | 'shortBreak' | 'longBreak'

// Helper function to play sound based on the timer mode
const playSoundForMode = (mode: TimerMode) => {
  const settings = useSettingsStore.getState()
  // Check if sound is enabled in settings
  if (!settings.soundEnabled) {
    return
  }

  let audioSrc
  switch (mode) {
    case 'focus':
      // Sound for when a break ends and focus period begins
      audioSrc = '/focue-end-new-notification-017-352293.mp3'
      break
    case 'shortBreak':
      // Sound for when a focus period ends and a short break begins
      audioSrc = '/break-end-new-notification-011-364050.mp3'
      break
    case 'longBreak':
      // Sound for when a focus period ends and a long break begins
      audioSrc = '/longbreak-end-new-notification-on-your-device-138695.mp3'
      break
    default:
      return // No sound for other states or invalid modes
  }

  if (audioSrc) {
    const audio = new Audio(audioSrc)
    audio.play().catch(error => console.error('Audio play failed:', error))
  }
}

// Helper function to send browser notifications
const sendBrowserNotification = async (mode: TimerMode, timeLeft: number) => {
  const settings = useSettingsStore.getState()
  if (!settings.notificationEnabled) {
    return
  }

  if (!('Notification' in window)) {
    console.warn('This browser does not support desktop notification')
    return
  }

  if (Notification.permission === 'granted') {
    const locale = settings.language || 'en' // Get current locale from settings
    const messages = (await import(`../../messages/${locale}.json`)).default

    let title = ''
    let body = ''
    const minutes = Math.floor(timeLeft / 1000 / 60)

    switch (mode) {
      case 'focus':
        title = messages.Notifications.focusEndTitle
        body = messages.Notifications.focusEndBody.replace('{minutes}', minutes.toString())
        break
      case 'shortBreak':
        title = messages.Notifications.shortBreakEndTitle
        body = messages.Notifications.shortBreakEndBody
        break
      case 'longBreak':
        title = messages.Notifications.longBreakEndTitle
        body = messages.Notifications.longBreakEndBody
        break
    }

    new Notification(title, { body })
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission().then(permission => {
      if (permission === 'granted') {
        sendBrowserNotification(mode, timeLeft) // Try again after permission granted
      }
    })
  }
}

interface TimerState {
  // Timer state
  timeLeft: number // Now stores time in milliseconds for finer granularity
  status: TimerStatus
  mode: TimerMode
  rounds: number
  animationFrameId: number | null // For requestAnimationFrame
  pomodoroHistory: Array<{ date: string; count: number }>

  // Actions
  startTimer: () => void
  pauseTimer: () => void
  resetTimer: () => void
  skipTimer: () => void
  setMode: (mode: TimerMode) => void
  tick: () => void
  animationTick: () => void // New method for smooth animation

  // Helper methods
  getCurrentDuration: () => number
  getFocusDuration: () => number
  getShortBreakDuration: () => number
  getLongBreakDuration: () => number
  getLongBreakInterval: () => number
  getDisplayTime: () => { minutes: number; seconds: number } // Helper for UI display

  // Settings synchronization
  syncWithSettings: () => void
}

// Helper function to get settings
const getSettings = () => useSettingsStore.getState()

// Helper function to get translations based on current language
const getTimerTranslations = async (locale: string) => {
  try {
    const messages = (await import(`../../messages/${locale}.json`)).default
    return messages.Timer
  } catch {
    // Fallback to English if translation fails
    const messages = (await import(`../../messages/en.json`)).default
    return messages.Timer
  }
}

// Helper function to update document title
const updateDocumentTitle = async (timeLeft: number, mode: TimerMode, status: TimerStatus) => {
  // If idle, restore original title
  if (status === 'idle') {
    document.title = 'Pomodo'
    return
  }

  // Convert milliseconds to seconds for display
  const timeInSeconds = Math.ceil(timeLeft / 1000)
  const minutes = Math.floor(timeInSeconds / 60)
  const seconds = timeInSeconds % 60
  const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`

  const settings = getSettings()
  const locale = settings.language || 'en'
  const timerTranslations = await getTimerTranslations(locale)

  let modeText = ''
  switch (mode) {
    case 'focus':
      modeText = timerTranslations.modes.focus
      break
    case 'shortBreak':
      modeText = timerTranslations.modes.shortBreak
      break
    case 'longBreak':
      modeText = timerTranslations.modes.longBreak
      break
  }

  let statusText = ''
  switch (status) {
    case 'running':
      statusText = ''
      break
    case 'paused':
      statusText = ` (${timerTranslations.status.paused})`
      break
  }

  document.title = `${modeText} - ${formattedTime}${statusText} | Pomodo`
}

export const useTimerStore = create<TimerState>()(
  persist(
    (set, get) => {
      // Initialize the worker as soon as the store is created
      initializeWorker(() => get().tick())

      return {
        // Initial state
        timeLeft: getSettings().workDuration * 60 * 1000,
        status: 'idle',
        mode: 'focus',
        rounds: 0,
        animationFrameId: null,
        pomodoroHistory: [],

        // Helper methods to get durations in milliseconds
        getFocusDuration: () => getSettings().workDuration * 60 * 1000,
        getShortBreakDuration: () => getSettings().shortBreakDuration * 60 * 1000,
        getLongBreakDuration: () => getSettings().longBreakDuration * 60 * 1000,
        getLongBreakInterval: () => getSettings().longBreakInterval,

        // Helper method to get display time
        getDisplayTime: () => {
          const timeInSeconds = Math.ceil(get().timeLeft / 1000)
          const minutes = Math.floor(timeInSeconds / 60)
          const seconds = timeInSeconds % 60
          return { minutes, seconds }
        },

        getCurrentDuration: () => {
          const state = get()
          switch (state.mode) {
            case 'focus':
              return state.getFocusDuration()
            case 'shortBreak':
              return state.getShortBreakDuration()
            case 'longBreak':
              return state.getLongBreakDuration()
            default:
              return state.getFocusDuration()
          }
        },

        // Actions
        startTimer: () => {
          const { animationFrameId, status } = get()
          if (status === 'running') return

          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId)
          }

          // Start the worker with the desired interval
          if (timerWorker) {
            timerWorker.postMessage({ command: 'start', newInterval: TIMER_INTERVAL })
          }

          // Use requestAnimationFrame for smooth wave animation updates
          let lastTime = 0
          const animateWave = (currentTime: number) => {
            if (currentTime - lastTime >= 16) {
              // ~60fps
              get().animationTick()
              lastTime = currentTime
            }

            if (get().status === 'running') {
              const newAnimationFrameId = requestAnimationFrame(animateWave)
              set({ animationFrameId: newAnimationFrameId })
            }
          }

          const { timeLeft, mode } = get()
          set({ status: 'running' })

          // Start animation loop
          const newAnimationFrameId = requestAnimationFrame(animateWave)
          set({ animationFrameId: newAnimationFrameId })

          updateDocumentTitle(timeLeft, mode, 'running').catch(console.error)
        },

        pauseTimer: () => {
          const { animationFrameId, timeLeft, mode } = get()

          if (timerWorker) {
            timerWorker.postMessage({ command: 'stop' })
          }

          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId)
          }
          set({
            status: 'paused',
            animationFrameId: null
          })
          updateDocumentTitle(timeLeft, mode, 'paused').catch(console.error)
        },

        resetTimer: () => {
          const { animationFrameId } = get()

          if (timerWorker) {
            timerWorker.postMessage({ command: 'stop' })
          }

          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId)
          }

          const timeLeft = getSettings().workDuration * 60 * 1000
          set({
            timeLeft: timeLeft,
            status: 'idle',
            mode: 'focus',
            rounds: 0,
            animationFrameId: null
          })
          updateDocumentTitle(timeLeft, 'focus', 'idle').catch(console.error)
        },

        skipTimer: async () => {
          const { mode, rounds, animationFrameId } = get()
          const longBreakInterval = get().getLongBreakInterval()

          if (timerWorker) {
            timerWorker.postMessage({ command: 'stop' })
          }

          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId)
          }

          if (mode === 'focus') {
            const newRounds = rounds + 1
            const nextMode = newRounds % longBreakInterval === 0 ? 'longBreak' : 'shortBreak'
            playSoundForMode(nextMode)
            await sendBrowserNotification(
              nextMode,
              nextMode === 'longBreak' ? get().getLongBreakDuration() : get().getShortBreakDuration()
            )
            set({
              mode: nextMode,
              rounds: newRounds,
              timeLeft:
                nextMode === 'longBreak' ? get().getLongBreakDuration() : get().getShortBreakDuration(),
              status: 'idle',
              animationFrameId: null
            })
          } else {
            playSoundForMode('focus')
            await sendBrowserNotification('focus', get().getFocusDuration())
            set({
              mode: 'focus',
              timeLeft: get().getFocusDuration(),
              status: 'idle',
              animationFrameId: null
            })
          }
          get().startTimer()
        },

        setMode: (mode: TimerMode) => {
          const { animationFrameId } = get()

          if (timerWorker) {
            timerWorker.postMessage({ command: 'stop' })
          }

          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId)
          }

          const state = get()
          let timeLeft: number
          switch (mode) {
            case 'focus':
              timeLeft = state.getFocusDuration()
              break
            case 'shortBreak':
              timeLeft = state.getShortBreakDuration()
              break
            case 'longBreak':
              timeLeft = state.getLongBreakDuration()
              break
            default:
              timeLeft = state.getFocusDuration()
          }

          set({
            mode,
            timeLeft,
            status: 'idle',
            animationFrameId: null
          })
          updateDocumentTitle(timeLeft, mode, 'idle').catch(console.error)
        },

        tick: async () => {
          const { timeLeft, mode, rounds } = get()
          const longBreakInterval = get().getLongBreakInterval()

          const newTimeLeft = timeLeft - TIMER_INTERVAL

          if (newTimeLeft < 0) {
            if (get().animationFrameId) {
              cancelAnimationFrame(get().animationFrameId as number)
            }

            if (mode === 'focus') {
              const newRounds = rounds + 1
              const nextMode = newRounds % longBreakInterval === 0 ? 'longBreak' : 'shortBreak'
              const nextTimeLeft =
                nextMode === 'longBreak' ? get().getLongBreakDuration() : get().getShortBreakDuration()
              playSoundForMode(nextMode)
              await sendBrowserNotification(nextMode, nextTimeLeft)

              const today = new Date().toISOString().slice(0, 10)
              const oldHistory = get().pomodoroHistory
              const todayEntry = oldHistory.find(item => item.date === today)
              const newHistory = todayEntry
                ? oldHistory.map(item =>
                  item.date === today ? { ...item, count: item.count + 1 } : item
                )
                : [...oldHistory, { date: today, count: 1 }]

              set({
                mode: nextMode,
                rounds: newRounds,
                timeLeft: nextTimeLeft,
                status: 'running',
                pomodoroHistory: newHistory
              })
              updateDocumentTitle(nextTimeLeft, nextMode, 'running').catch(console.error)
              get().startTimer() // Restart timer for the next session
            } else {
              const nextTimeLeft = get().getFocusDuration()
              playSoundForMode('focus')
              await sendBrowserNotification('focus', nextTimeLeft)
              set({
                mode: 'focus',
                timeLeft: nextTimeLeft,
                status: 'running'
              })
              updateDocumentTitle(nextTimeLeft, 'focus', 'running').catch(console.error)
              get().startTimer() // Restart timer for the next session
            }
          } else {
            set({ timeLeft: newTimeLeft })
            updateDocumentTitle(newTimeLeft, mode, 'running').catch(console.error)
          }
        },

        animationTick: () => {
          const state = get()
          if (state.status === 'running') {
            set({ timeLeft: state.timeLeft })
          }
        },

        syncWithSettings: () => {
          const { status, mode } = get()
          if (status === 'idle' || status === 'paused') {
            const newTimeLeft = get().getCurrentDuration()
            set({ timeLeft: newTimeLeft })
            updateDocumentTitle(newTimeLeft, mode, status).catch(console.error)
          }
        }
      }
    },
    {
      name: 'timer-store',
      onRehydrateStorage: () => {
        return state => {
          if (state) {
            if (state.status === 'running') {
              state.status = 'paused'
            }
            state.animationFrameId = null
          }
        }
      },
      partialize: state =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => !['animationFrameId'].includes(key))
        )
    }
  )
)

// Subscribe to SettingsStore changes
let previousSettings = {
  workDuration: 0,
  shortBreakDuration: 0,
  longBreakDuration: 0,
  longBreakInterval: 0
};

// Initialize previousSettings with current values
const initSettings = useSettingsStore.getState();
previousSettings = {
  workDuration: initSettings.workDuration,
  shortBreakDuration: initSettings.shortBreakDuration,
  longBreakDuration: initSettings.longBreakDuration,
  longBreakInterval: initSettings.longBreakInterval
};

useSettingsStore.subscribe((state) => {
  const currentSettings = {
    workDuration: state.workDuration,
    shortBreakDuration: state.shortBreakDuration,
    longBreakDuration: state.longBreakDuration,
    longBreakInterval: state.longBreakInterval
  };

  // Only trigger syncWithSettings if duration settings have actually changed
  const hasRelevantChanges =
    previousSettings.workDuration !== currentSettings.workDuration ||
    previousSettings.shortBreakDuration !== currentSettings.shortBreakDuration ||
    previousSettings.longBreakDuration !== currentSettings.longBreakDuration ||
    previousSettings.longBreakInterval !== currentSettings.longBreakInterval;

  if (hasRelevantChanges) {
    useTimerStore.getState().syncWithSettings();
    previousSettings = currentSettings;
  }
});